import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, fromEvent, Observable, of, Subject, Subscription } from 'rxjs';
import { map, tap, take, debounceTime, takeUntil } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';
import has from 'lodash-es/has';
import pick from 'lodash-es/pick';
import cloneDeep from 'lodash-es/cloneDeep';

import { SortService } from '@finder/features/list/services/sort/sort.service';
import { ISortOptions } from '@finder/features/list/services/sort/sort-options.interface';
import { IFilterOptions } from '@finder/features/list/services/list/filter-options.interface';
import { IFilterSortOptions } from '@finder/features/list/services/list/filter-sort-options.interface';
import { IFilterSortObject } from '@finder/features/list/services/list/filter-sort-object.interface';
import { IListGroup } from './group.interface';
import { SsrService } from '@finder/shared/services/ssr/ssr.service';

@Injectable()
export class ListService implements OnDestroy {
    /**
     * Original List Data.
     */
    listData: Array<any>;
    locations: Array<any>;
    disneyPicks: Array<any>;
    filteredList: Array<any>;
    groupedList: any;
    groups: Array<IListGroup>;
    availability: any;
    filterOptions: IFilterOptions;
    sortOptions: ISortOptions;
    filterSortListCache = {};
    currentGroup: number;
    currentSubgroup: number;
    maxItems: number;

    destroy$ = new Subject<void>();

    // This is passed to the web-worker to update the list entries
    labels: any;

    // BehaviorSubject will emit the last value emitted when subscribe
    // even if that already happened.
    // This is to make sure in the list component template when we are using
    // 'async' pipe it load the data on the first page view.
    groupedListSubject = new BehaviorSubject<any>({});

    private listenForFilterRequests$ = new Subject();

    constructor(
        private sortService: SortService,
        private ssrService: SsrService,
    ) {
        this.listenForFilterRequests();
    }

    ngOnDestroy() {
        /* istanbul ignore next */
        this.destroy$.next();
        this.destroy$.complete();
    }

    resetPartialData() {
        this.currentGroup = 0;
        this.currentSubgroup = 0;

        this.groups?.forEach((group: IListGroup) => {
            group.next = false;
            group.page = 1;

            if (group.subgroups?.length) {
                group.subgroups.forEach((subgroup: IListGroup) => {
                    subgroup.next = false;
                    subgroup.page = 1;
                });
            }
        });
    }

    updateListData(listData: any, defaultSortOption: string, sortOrdering?: Array<string>, maxItems?: number): BehaviorSubject<any> {
        this.listData = listData.results;
        this.locations = listData.locations;
        this.maxItems = maxItems;
        this.disneyPicks = listData.disneyPicks;
        this.sortOptions = {
            type: defaultSortOption,
        };
        if (sortOrdering) {
            this.sortOptions.ordering = sortOrdering;
        }
        this.filterOptions = {
            appliedFacets: [],
            locations: [],
        };
        this.availability = {};
        this.filterSortOptions({ filter: this.filterOptions, sort: this.sortOptions });

        return this.groupedListSubject;
    }

    /**
     * @name        updatePartialListData
     * @description loads a section of items in the component list, if all the items of
     *              a group are already loaded, go to the next one to start loading this
     * @param       partialList
     * @returns     void
     */
    updatePartialListData(partialList: any) {
        if (!this.groups?.length) {
            return;
        }

        // if the 'next' attribute is set to true it is necessary to validate the next group
        if (this.groups[this.currentGroup]?.next) {
            this.currentGroup++;
            this.currentSubgroup = 0;
        }

        this.addNewItems(
            partialList,
            this.groupedList,
            this.groups[this.currentGroup]
        );
    }

    sortByAvailability(availabilityData) {
        this.filterSortOptions({
            sort: this.sortOptions,
            filter: this.filterOptions,
            availability: availabilityData
        });
    }

    /**
     * It will merge filter and sort options with the saved options
     * in the service and call the memoized function to run the sort and filter logic,
     * after its done it will emit a new value (IFilterSortObject) in the groupedListSubject Observable.
     *
     * @param options the filter and sort options.
     * Example:
     * input => options = {
     *      filter: appliedFacets: [{
     *          id: 'thrill-rides',
     *          label: 'Thrill Rides',
     *          value: true
     *      }],
     *      sort: { type: 'name' }
     *  }
     */
    filterSortOptions(options: Partial<IFilterSortOptions>) {
        /**
         * This method is spammed a few times if a filter is applied. This will
         * filter through a debounce pipe and then process the filter so it only
         * hits the web-worker once.
         *
         * this.listenForFilterRequests() handles the request
         */
        this.listenForFilterRequests$.next(options);
    }

    sort(options: ISortOptions, list: Array<any>): any {
        return this.sortService.sort({
            list,
            featuredIds: this.disneyPicks,
            ...options
        });
    }

    workerWrapper(path: string) {
        return new Worker(path);
    }

    filter(options: IFilterOptions, list: Array<any>): Observable<any[]> {
        // @SSR: The Worker class doesn't exist on the server. Just return the data to the method.
        if (this.ssrService.isSSR()) {
            return of(list);
        }

        const filterWorker = this.workerWrapper('./advanced-finder/web-workers/list-filter.js');
        // Pass the labels into the web-worker so we can update the list-cards when multiple filters are applied
        const labels = this.labels.card;

        const worker$ = fromEvent(filterWorker, 'message')
            .pipe(
                map((event: any) => {
                    filterWorker.terminate();

                    return event.data;
                }),
                take(1)
            );

        filterWorker.postMessage({
            options,
            list,
            labels
        });

        return worker$;
    }

    getGroupsAndMarkers(groupedList: any, hasSubGroups: boolean) {
        const groups: Array<IListGroup> = [];
        let markers = [];

        Object.keys(groupedList).forEach((key) => {
            if (!hasSubGroups) {
                const cards = groupedList[key];
                groups.push(this.setGroup(key, cards));

                const marker = this.setMarkers(cards);
                markers = markers.concat(marker);

                return;
            }

            const group: IListGroup = groupedList[key];
            const subgroups: Array<IListGroup> = [];
            let length = 0;

            Object.keys(group).forEach((subgroupKey: string) => {
                const cards: any = group[subgroupKey];

                subgroups.push(this.setGroup(subgroupKey, cards));

                const marker = this.setMarkers(cards);
                markers = markers.concat(marker);

                length += cards.length;
            });

            groups.push({
                key,
                subgroups,
                length,
                page: 1,
                next: false,
            });
        });

        return {
            groups,
            markers
        };
    }

    /**
     * @name        addNewItems
     * @description validate the groups and subgroups and load the list with
     *              the new items in the order of the groups
     * @param       partialList the component list
     * @param       groupedList the lists ordered and filtered by each group
     * @param       group the current group to get the items section of the list
     * @returns     void
     */
    private addNewItems(partialList: any, groupedList: any, group: IListGroup) {
        if (!group) {
            return;
        }
        // Check if the groupedList has a sub group
        if (!groupedList?.[group.key]?.length) {
            const subgroups: IListGroup[] = group.subgroups;
            const subgroup: IListGroup = subgroups?.[this.currentSubgroup];

            // if the partial list does not have an object for the subgroup,
            // then you must create a new one
            if (!partialList[group.key]) {
                partialList[group.key] = {};
            }

            // if the 'next' attribute is true, a new subgroup should be loaded
            if (subgroup?.next) {
                this.currentSubgroup++;
            }

            this.addNewItems(
                partialList[group.key],
                groupedList?.[group.key],
                subgroups?.[this.currentSubgroup]
            );
            // after subgroups are done,
            // we need to check other upper groups
            const totalMaxItems = group.page * this.maxItems;
            const isTheLastSubgroup = this.currentSubgroup === subgroups?.length - 1;
            if (totalMaxItems >= groupedList?.[group?.key]?.[subgroup?.key]?.length && isTheLastSubgroup) {
                group.next = true;
            }

            group.page++;

            return;
        }

        // if the partial List no has items yet,
        // then it's necessary create a new array
        if (!partialList[group.key]) {
            partialList[group.key] = [];
        }

        const fromItem = (group.page - 1) * this.maxItems;
        const toItem = group.page * this.maxItems;
        const newItems = groupedList?.[group.key].slice(fromItem, toItem);

        partialList[group.key].push(...newItems);

        // if the range of items is mayor than the groupedList size
        // the attribute next is set to true to load a new group
        if (toItem >= groupedList?.[group.key]?.length) {
            group.next = true;
        }

        group.page++;
    }

    private listenForFilterRequests() {
        this.listenForFilterRequests$.asObservable()
            .pipe(
                /**
                 * The filter is called a number of times if there is a filter applied to the
                 * list. This will wait for all of the requests and then process the filter
                 */
                debounceTime(100),
                takeUntil(this.destroy$)
            ).subscribe((options: any) => {
                const { sort, filter, availability } = options;

                this.filterOptions = Object.assign(this.filterOptions, filter);
                this.sortOptions = Object.assign(this.sortOptions, sort);
                this.availability = Object.assign(this.availability, availability);

                this.memoizedFilterSortList({
                    sort: this.sortOptions,
                    filter: this.filterOptions,
                    availability: this.availability
                })
                .pipe(
                    // Unsubscribe from the request after it's filled
                    take(1)
                )
                .subscribe((result) => {
                    this.groupedList = result.list;
                    this.groups = result.groups;
                    // notify subscribers of new filter/sorted list.
                    this.groupedListSubject.next(result);
                });
            });
    }

    private memoizedFilterSortList(args: Partial<IFilterSortOptions>): Observable<IFilterSortObject> {
        const key = JSON.stringify(args);
        if (key in this.filterSortListCache) {
            return of(this.filterSortListCache[key]);
        }

        return this.filterSortList(args)
            .pipe(
                tap(result => this.filterSortListCache[key] = result)
            );
    }

    /**
     * Execute the filter and sort logic
     * based on the options passed by params.
     *
     * Example:
     * input => options = {
     *      filter: appliedFacets: [{
     *          id: 'thrill-rides',
     *          label: 'Thrill Rides',
     *          value: true
     *      }],
     *      sort: { type: 'name' }
     *  }
     *  output => {
     *      group: [{key: 'all', landName: 'Land Name', locationName: 'Location Name'}],
     *      list: {all: [..], closedForRefurbishment: [..]},
     *      size: 36,
     * }
     *
     * @param options the filter sort options.
     * @returns the filter and sort object.
     */
    private filterSortList(options: Partial<IFilterSortOptions>): Observable<IFilterSortObject> {
        const { sort, filter } = options;
        // Filter the list

        return this.filter(filter, this.listData)
            .pipe(
                map(data => {
                    this.filteredList = data;
                    const hasAvailability = !isEmpty(this.availability);
                    // assign availability data
                    if (hasAvailability) {
                        this.filteredList = this.applyAvailability(data, this.availability);
                        // enable sort by availability
                        sort.withAvailability = true;
                    }

                    // Sort the list
                    this.groupedList = this.sort(sort, this.filteredList);

                    const result = {
                        list: this.groupedList,
                        size: this.filteredList?.length ?? 0,
                        ...this.getGroupsAndMarkers(this.groupedList, hasAvailability)
                    };

                    return result;
                })
            );
    }

    private applyAvailability(data: any[], availability: any) {
        return data.map(card => {
            if (!availability[card.id]) {
                return card;
            }
            const resultAvailability = cloneDeep(availability[card.id]);

            if (resultAvailability.multipleLocations) {
                resultAvailability.multipleLocations.locations = this.getLocationsData(
                    this.listData,
                    resultAvailability.multipleLocations.locations
                );
            }

            if (resultAvailability.hasAvailability) {
                resultAvailability.availabilitySearchDate = new Date(
                    resultAvailability.availabilitySearchDate
                );
            }

            return Object.assign(card, resultAvailability);
        });
    }

    private getLocationsData(data: any[], locations: any[]) {
        return locations.reduce((result, location) => {
            const locationData = data.find(card => card.id === location.id);
            if (!locationData) {
                return result;
            }

            result.push({
                ...location,
                ...pick(locationData, ['name', 'locationName', 'facetsLabel'])
            });

            return result;
        }, []);
    }

    private setGroup(key: string, value: any): IListGroup {
        // The 'page' and 'next' attributes was added to
        // load data sections  of the group
        return {
            key,
            locationName: !isEmpty(value) && value[0].locationName,
            landName: !isEmpty(value) && value[0].landName,
            entityType: !isEmpty(value) && value[0].entityTypeName,
            page: 1,
            next: false,
        };
    }

    private setMarkers(cards) {
        return cards.reduce((dest, card) => {
            // Stop if there's no location data
            if (!has(card, 'marker')) {
                return dest;
            }

            if (card.closedForRefurbishment) {
                card.marker.card.closedForRefurbishment = card.closedForRefurbishment;
            }

            // Add a new array of markers for the filtered location
            // If there's nothing in the filter this is skipped
            card.filteredMarkers = {
                counter: 0,
                markers: []
            };
            this.filterOptions.locations.forEach(location => {
                // If we're missing the location just stop
                if (!card.parkLocations?.[location]) {
                    return;
                }

                // Create a shallow copy of the park location so we can change it
                const parkLocation = { ... card.parkLocations[location] };

                // Set the location from the card to the park location so when it's copied it has the right value
                parkLocation.pin = card.marker.pin;

                // Add the new filtered markers to the card
                card.filteredMarkers.markers.push({
                    // Start with an empty object so the original marker card properties aren't changed
                    card: Object.assign({}, card.marker.card, { land: parkLocation.name }),
                    ... parkLocation
                });
            });

            // If we have filtered markers copy them to the dest obj. Otherwise just add the one
            dest.push( ... (card.filteredMarkers.markers.length ? card.filteredMarkers.markers : [card.marker]));

            return dest;
        }, []);
    }
}
